import React, { FC } from "react";

import ModifierGroup from "./modifier-group";
import {
  ComponentCatalogModifierListInfo,
  CatalogItem,
  CatalogModifierList,
  Enum_Catalogmodifierlist_Selection_Type,
} from "../../queries/auto-generated/schemas";
import { cardContentPadding } from "./item";
import { Button, Divider, notification, Input } from "antd";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../ducks";
import { BuiltItem } from "../../types";
import { prettyMoney, calculatePrice } from "../../helpers";
import cart from "../../ducks/cart";
import catalog from "../../ducks/catalog";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import VariationGroup from "./variation-group";
import { useHistory } from "react-router-dom";
import { CHECKOUT_PAGE_PATH } from "../../router";
import _ from "lodash";

const { TextArea } = Input;
const DELAY_TIME_MS = 100;

const SelectionForm: FC<{ catalogItem: CatalogItem }> = ({ catalogItem }) => {
  const dispatch = useDispatch();
  const choices = useSelector(
    (state: RootState) => state.catalog.currentItemSelectionChoices
  );
  const modifierLists = useSelector(
    (state: RootState) => state.catalog.data.modifierList
  );
  const modifiers = useSelector(
    (state: RootState) => state.catalog.data.modifiers
  );
  const variations = useSelector(
    (state: RootState) => state.catalog.data.variations
  );
  const currentEditedLineItemIndex = useSelector(
    (state: RootState) => state.cart.currentEditedLineItemIndex
  );
  const history = useHistory();

  const pushHintsNotification = () => {
    const READY_FOR_CHECKOUT = "READY_FOR_CHECKOUT";
    notification.open({
      placement: "bottomRight",
      key: READY_FOR_CHECKOUT,
      message: (
        <StyledButton
          type="text"
          onClick={() => {
            history.push(CHECKOUT_PAGE_PATH);
            notification.close(READY_FOR_CHECKOUT);
          }}
        >
          Go to checkout
        </StyledButton>
      ),
    });
  };

  const price = calculatePrice(modifiers, variations, choices);
  const prettyPrice = prettyMoney(price);

  const handleClick = () => {
    if (!choices) {
      return;
    }
    const errors = verifySelections(choices, catalogItem, modifierLists);
    if (errors.length === 0) {
      if (currentEditedLineItemIndex === undefined) {
        dispatch(cart.actions.add({ selection: choices }));
        pushHintsNotification();
      } else {
        dispatch(cart.actions.replaceEditedLineItem({ builtItem: choices }));
        dispatch(cart.actions.cancelEditLineItem());
      }
      dispatch(catalog.actions.cancelSelection());
    } else {
      dispatch(catalog.actions.updateSelectionError({ error: true }));
    }
  };

  const updateNoteDebounced = _.debounce(
    (value: string) => {
      dispatch(catalog.actions.updateSelectionNote({ note: value }));
    },
    DELAY_TIME_MS,
    { maxWait: 1000 }
  );

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateNoteDebounced(e.target.value);
  };

  const buttonWording =
    currentEditedLineItemIndex === undefined ? "Add to Order" : "Update Order";

  return (
    <Container>
      {(catalogItem?.variations?.length ?? 0) > 1 && (
        <VariationGroup catalogItem={catalogItem} />
      )}
      <ModifierList catalogItem={catalogItem} />
      <StyledNotesContainer>
        <label>
          <h3>Notes</h3>
        </label>
        <TextArea
          rows={2}
          onChange={handleNoteChange}
          defaultValue={choices?.note}
          maxLength={100}
        />
        <small>Max length: 100 characters</small>
      </StyledNotesContainer>
      <Divider style={{ margin: 0 }} />
      <Bottom padding={cardContentPadding}>
        <QuantityController />
        <Button
          block
          shape="round"
          type="primary"
          size="large"
          onClick={handleClick}
        >{`${buttonWording} | ${prettyPrice}`}</Button>
      </Bottom>
    </Container>
  );
};

const ModifierList: FC<{
  catalogItem: CatalogItem;
}> = ({ catalogItem }) => {
  const catalogItemsWithIds =
    catalogItem?.modifier_list_info?.filter((i) => i?.id) ?? [];

  return catalogItemsWithIds.length > 0 ? (
    <Choices style={{ padding: cardContentPadding }}>
      {catalogItemsWithIds.map((info) => {
        return (
          <ModifierGroup
            info={info as ComponentCatalogModifierListInfo}
            catalogItemId={catalogItem?.id}
          />
        );
      })}
    </Choices>
  ) : null;
};

const QuantityController: FC = () => {
  const quantity = useSelector(
    (state: RootState) => state.catalog.currentItemSelectionChoices?.quantity
  );
  const dispatch = useDispatch();

  const updateQuantity = (change: number) => {
    dispatch(catalog.actions.updateSelectionQuantity({ change }));
  };
  return (
    <QuantityControllerContainer>
      <span style={{ padding: "0.5em" }}>Quantity</span>
      <Button
        type="ghost"
        shape="circle"
        size="large"
        icon={<MinusOutlined />}
        onClick={() => updateQuantity(-1)}
      ></Button>
      <Quantity>{quantity}</Quantity>
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={<PlusOutlined />}
        onClick={() => updateQuantity(1)}
      ></Button>
    </QuantityControllerContainer>
  );
};

const QuantityControllerContainer = styled.div`
  padding: 0 1em 1em;
  text-align: right;
`;
const Quantity = styled.span`
  padding: 0.5em;
  font-weight: bold;
`;

const verifySelections = (
  selection: BuiltItem,
  catalogItem: CatalogItem,
  modifierLists: { [key: string]: CatalogModifierList }
): string[] => {
  let errors: string[] = [];

  if (catalogItem.id !== selection.item_id) {
    errors.push("ids don't match");
  }

  if (!selection.variation_id) {
    errors.push("variation not set");
  }

  catalogItem.modifier_list_info?.forEach((info) => {
    const modInfo = selection.modifications[info?.id ?? ""];
    const length = modInfo.selected.length;
    if (
      info?.max_selected_modifiers &&
      info?.max_selected_modifiers !== -1 &&
      length > info?.max_selected_modifiers
    ) {
      errors.push("too many modifiers");
    }
    if (
      info?.min_selected_modifiers &&
      info?.min_selected_modifiers !== -1 &&
      length < info.min_selected_modifiers
    ) {
      errors.push("too few modifiers");
    }
    const modifierList = modifierLists?.[info?.modifier_list?.id ?? ""];
    if (
      info?.max_selected_modifiers === -1 &&
      info?.min_selected_modifiers === -1 &&
      modifierList &&
      modifierList.selection_type ===
        Enum_Catalogmodifierlist_Selection_Type.Single &&
      length > 1
    ) {
      errors.push("too many modifiers for a single selection type");
    }
  });

  return errors;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;
const Choices = styled.div``;

const Bottom = styled.div<{ padding: string }>`
  padding: ${(p) => p.padding};
`;

const StyledNotesContainer = styled.div`
  padding: 0 24px 24px;
`;

const StyledButton = styled(Button)`
  span {
    color: rgba(0, 0, 0, 0.85);
    text-decoration: underline;
    text-decoration-color: #ffeb19;
  }
`;

export default SelectionForm;
